/**
 * Unit conversion constants used when converting PDF drawing points into real-world measurements.
 */
const FT_TO_M   = 0.3048;
const M2_TO_SQFT = 10.7639;
const M_TO_FT    = 3.28084;

/**
 * Parses a drawing scale string and returns the number of drawing inches per real-world foot.
 *
 * Supported examples include `1/4" = 1"` and `0.25" = 1"`. Invalid or empty
 * values fall back to 0.25 inches per foot.
 */
export function parseScaleString(scaleStr: string): number {
  if (!scaleStr) return 0.25;
  const s = scaleStr.trim().replace(/'/g, '"');
  let m = s.match(/^(\d+)\/(\d+)"?\s*=\s*1"/);
  if (m) return +m[1] / +m[2];
  m = s.match(/^(\d+\.?\d*)"?\s*=\s*1"/);
  if (m) return +m[1];
  return 0.25;
}

/**
 * Converts a PDF point into meters using the active drawing scale.
 */
function metersPerPdfPoint(inchesPerFoot: number): number {
  return (1 / inchesPerFoot / 72) * FT_TO_M;
}

/**
 * Calculates polygon area in PDF point units using the shoelace formula.
 */
function shoelaceArea(points: [number, number][]): number {
  const n = points.length;
  if (n < 3) return 0;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % n];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s) * 0.5;
}

/**
 * Calculates the closed polygon perimeter in PDF point units.
 */
function polygonPerimeterPts(points: [number, number][]): number {
  const n = points.length;
  if (n < 2) return 0;
  let p = 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % n];
    p += Math.hypot(x2 - x1, y2 - y1);
  }
  return p;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

function decimalToFraction(decimal: number, maxDen = 64): string {
  if (decimal >= 1) {
    const whole = Math.floor(decimal);
    const rem = decimal - whole;
    if (rem < 0.005) return `${whole}`;
    return `${whole} ${decimalToFraction(rem, maxDen)}`;
  }
  let bestNum = 1, bestDen = 1, bestErr = Infinity;
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(decimal * d);
    if (n === 0) continue;
    const err = Math.abs(decimal - n / d);
    if (err < bestErr) { bestErr = err; bestNum = n; bestDen = d; }
  }
  const g = gcd(bestNum, bestDen);
  const num = bestNum / g, den = bestDen / g;
  return den === 1 ? `${num}` : `${num}/${den}`;
}

/**
 * Formats a scale string for display, converting any decimal inch value to the
 * nearest simple fraction so `0.052" = 1'` renders as `1/19" = 1'`.
 * Strings that already contain a `/` (standard scales) are returned unchanged.
 */
export function formatScaleLabel(scaleStr: string): string {
  if (!scaleStr) return scaleStr;
  if (scaleStr.includes('/')) return scaleStr;
  const m = scaleStr.trim().match(/^(\d+\.?\d*)"?\s*=\s*1['"]?/);
  if (!m) return scaleStr;
  const val = parseFloat(m[1]);
  if (!Number.isFinite(val) || val <= 0) return scaleStr;
  return `${decimalToFraction(val)}" = 1'`;
}

/**
 * Computes real-world room metrics from a polygon drawn in PDF coordinates.
 *
 * The returned area is square feet, the perimeter is feet, and the corner count
 * is the number of polygon vertices.
 */
export function computeRoomMetrics(
  points: [number, number][],
  inchesPerFoot: number,
): { areaSqft: number; perimeterFt: number; corners: number } {
  const mPerPt     = metersPerPdfPoint(inchesPerFoot);
  const areaM2     = shoelaceArea(points) * mPerPt ** 2;
  const perimeterM = polygonPerimeterPts(points) * mPerPt;
  return {
    areaSqft:    areaM2     * M2_TO_SQFT,
    perimeterFt: perimeterM * M_TO_FT,
    corners:     points.length,
  };
}
